import { APIService } from "../apiService";
import { AuthenticationService } from "../authenticationService";
import { PurchaseOrder } from "src/types/database/purchaseOrder";
import { PaginatedPurchaseOrders, PaginatedPurchaseOrdersWithEuRelabelStatus, S3SignedUrl } from "src/types";
import {
  GetPurchaseOrdersQueryParams,
  GetPurchaseOrdersByBarcodeQueryParams,
  GetPurchaseOrdersForEuRelabelQueryParams,
  GetPurchaseOrdersForRelabelCaseBuilderQueryParams,
  GetPurchaseOrderIdsQueryParams,
  GetPurchaseOrderByIdQueryParams,
  GetPurchaseOrderByASNQueryParams,
  PatchPurchaseOrderShipmentDetailsRequestBody,
  PatchPurchaseOrderOpsNoteRequestBody,
  PatchPurchaseOrderRequestBody,
  PatchPurchaseOrderInvoiceRequestBody,
  PatchPurchaseOrderInvoiceStatusRequestBody,
  PatchPurchaseOrderComplianceRequestBody,
  PostPurchaseOrderCancellationRequestBody,
  PostPurchaseOrderPDFRequestBody,
  PostPurchaseOrdersExportsRequestBody,
  PostPurchaseOrderEmailRequestBody,
  PostPurchaseOrderConfirmationRequestBody,
  PostPurchaseOrderInvoiceUrlRequestBody,
  PostPurchaseOrderComplianceUrlRequestBody,
  PostPurchaseOrderPackingListUrlRequestBody,
  PostPurchaseOrdersImportsResponse,
  PostPurchaseOrdersManualRequestBody,
  PostPurchaseOrdersManualResponse,
  PostRerunParcelHeroRequestBody,
  PostChildPurchaseOrderRequestBody,
  PatchPurchaseOrderPaymentTermsRequestBody,
  PatchScheduledPOApprovalRequestBody
} from "src/types/api";

class PurchaseOrderAPIService extends APIService {
  constructor(authenticationService: AuthenticationService) {
    super(authenticationService, "/v1/purchase-orders");
  }

  async getPurchaseOrders(queryParams: GetPurchaseOrdersQueryParams): Promise<PaginatedPurchaseOrders> {
    return await this.jwtRequest("GET", undefined, undefined, queryParams);
  }

  async getPurchaseOrdersByBarcode(
    queryParams: GetPurchaseOrdersByBarcodeQueryParams
  ): Promise<PaginatedPurchaseOrders> {
    return await this.jwtRequest("GET", "/barcode", undefined, queryParams);
  }

  async getPurchaseOrdersForEuRelabel(
    queryParams: GetPurchaseOrdersForEuRelabelQueryParams
  ): Promise<PaginatedPurchaseOrdersWithEuRelabelStatus> {
    return await this.jwtRequest("GET", "/eu-relabel-candidates", undefined, queryParams);
  }

  async getPurchaseOrdersForRelabelCaseBuilder(
    queryParams: GetPurchaseOrdersForRelabelCaseBuilderQueryParams
  ): Promise<PaginatedPurchaseOrdersWithEuRelabelStatus> {
    return await this.jwtRequest("GET", "/relabel-case-builder-candidates", undefined, queryParams);
  }

  async getPurchaseOrderIds(queryParams: GetPurchaseOrderIdsQueryParams): Promise<string[]> {
    const path = `/ids`;

    return await this.jwtRequest("GET", path, undefined, queryParams);
  }

  async getPurchaseOrderById(
    purchaseOrderId: string,
    queryParams: GetPurchaseOrderByIdQueryParams
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}`;

    return await this.jwtRequest("GET", path, undefined, queryParams);
  }

  async getPurchaseOrderByAsn(asn: string, queryParams: GetPurchaseOrderByASNQueryParams): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(asn);
    const path = `/asn/${pathParam}`;

    return await this.jwtRequest("GET", path, undefined, queryParams);
  }

  async postPurchaseOrderConfirmation(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderConfirmationRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/confirmation`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postPurchaseOrderApproval(purchaseOrderId: string, createBackorderPO: boolean = false): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/confirmation/approval`;

    return await this.jwtRequest("POST", path, { createBackorderPO });
  }

  async postPurchaseOrderCancellation(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderCancellationRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/cancellation`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postPurchaseOrderInvoiceUrls(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderInvoiceUrlRequestBody
  ): Promise<S3SignedUrl[]> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/invoice-urls`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postPurchaseOrderComplianceUrls(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderComplianceUrlRequestBody
  ): Promise<S3SignedUrl[]> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/compliance-urls`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async patchPurchaseOrderInvoice(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderInvoiceRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/invoice`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async patchPurchaseOrderInvoiceStatus(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderInvoiceStatusRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/invoice-status`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async patchPurchaseOrderCompliance(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderComplianceRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/compliance`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async postPurchaseOrderPackingListUrls(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderPackingListUrlRequestBody
  ): Promise<S3SignedUrl[]> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/packing-list-urls`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async patchPurchaseOrderShipmentDetails(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderShipmentDetailsRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/shipment-details`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async patchPurchaseOrder(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async postChildPurchaseOrder(
    parentPurchaseOrderId: string,
    requestBody: PostChildPurchaseOrderRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(parentPurchaseOrderId);
    const path = `/${pathParam}/child`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postChildPurchaseOrderConfirmation(childPurchaseOrderId: string): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(childPurchaseOrderId);
    const path = `/${pathParam}/child-confirmation`;

    return await this.jwtRequest("POST", path, {});
  }

  async patchPurchaseOrderOpsNote(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderOpsNoteRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/ops-note`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async postPurchaseOrdersImportUrl(): Promise<S3SignedUrl> {
    const path = `/import-url`;

    return await this.jwtRequest("POST", path);
  }

  async postPurchaseOrdersImports(importFileId: string): Promise<PostPurchaseOrdersImportsResponse> {
    const pathParam = encodeURIComponent(importFileId);
    const path = `/${pathParam}/imports`;

    return await this.jwtRequest("POST", path);
  }

  async postPurchaseOrdersManual(
    requestBody: PostPurchaseOrdersManualRequestBody
  ): Promise<PostPurchaseOrdersManualResponse> {
    const path = `/manual`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postPurchaseOrderPDFUrl(purchaseOrderId: string, fileType: "PDF" | "CSV" = "PDF"): Promise<S3SignedUrl> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/pdf-url`;

    return await this.jwtRequest("POST", path, undefined, { fileType });
  }

  async postPurchaseOrderCommercialInvoiceUrl(
    purchaseOrderId: string,
    fileType: "PDF" | "CSV" = "PDF"
  ): Promise<S3SignedUrl> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/commercial-invoice-url`;

    return await this.jwtRequest("POST", path, undefined, { fileType });
  }

  async postPurchaseOrderPDF(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderPDFRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/pdf`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postPurchaseOrderEmail(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderEmailRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/email`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async deletePurchaseOrder(purchaseOrderId: string): Promise<void> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}`;

    return await this.jwtRequest("DELETE", path);
  }

  async postPurchaseOrdersExports(requestBody: PostPurchaseOrdersExportsRequestBody): Promise<S3SignedUrl> {
    const path = `/exports`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async postRerunParcelHero(
    purchaseOrderId: string,
    requestBody?: PostRerunParcelHeroRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/rerun-parcel-hero`;

    return await this.jwtRequest("POST", path, requestBody || {});
  }

  async getParcelHeroShipmentDetails(purchaseOrderId: string): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/parcel-hero-shipment`;

    return await this.jwtRequest("GET", path);
  }

  async postCancelParcelHeroShipment(purchaseOrderId: string): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/cancel-parcel-hero-shipment`;

    return await this.jwtRequest("POST", path);
  }

  async putRescheduleParcelHeroShipment(purchaseOrderId: string, collectionDate: string): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/reschedule-parcel-hero-shipment`;

    return await this.jwtRequest("PUT", path, { collectionDate });
  }

  async resolveGXOError(
    purchaseOrderId: string,
    requestBody: {
      products: { productId: string; barcode: string }[];
      estimatedDeliveryDate?: string;
    }
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/resolve-gxo-error`;

    return await this.jwtRequest("POST", path, requestBody);
  }

  async patchPurchaseOrderPaymentTerms(
    purchaseOrderId: string,
    requestBody: PatchPurchaseOrderPaymentTermsRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/payment-terms`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }

  async getVendorBackorders(
    vendorId: string,
    queryParams?: { limit?: number; pageToken?: string }
  ): Promise<PaginatedPurchaseOrders> {
    const pathParam = encodeURIComponent(vendorId);
    const path = `/vendor/${pathParam}/backorders`;

    return await this.jwtRequest("GET", path, undefined, queryParams);
  }

  async patchBackorderScheduledDate(purchaseOrderId: string, scheduledDate: string): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/scheduled-date`;

    return await this.jwtRequest("PATCH", path, { scheduledDate });
  }

  async patchScheduledPOApproval(
    purchaseOrderId: string,
    requestBody: PatchScheduledPOApprovalRequestBody
  ): Promise<PurchaseOrder> {
    const pathParam = encodeURIComponent(purchaseOrderId);
    const path = `/${pathParam}/scheduled-approval`;

    return await this.jwtRequest("PATCH", path, requestBody);
  }
}

export { PurchaseOrderAPIService };
